import { useCallback } from 'react'
import {
  DEFAULT_COMMENT_ORIENTATION,
  DEFAULT_NEW_COMMENT_TYPE,
  commentTypePresets,
  getCommentType,
} from '@constants'
import { useCommentMutations } from '@scene'
import { useSnackbar } from '@ui/snackbar'
import { useCommentsStore } from '@editorStores'
import { useGuidToElementIncludingRips } from '@editorHooks'
import { getShapeCenterPoint } from '../../utils'

// The single Positionsplan comment (if any) currently linked to the given element.
const findLinkedPlanComment = (elementGuid: string) =>
  useCommentsStore
    .getState()
    .comments.find(
      comment =>
        comment.element_guid === elementGuid && getCommentType(comment) === 'position-plan',
    )

/**
 * Reconciles the linked Positionsplan comment of an element with the desired "Plan" state from the
 * detail form. Used on submit (not on every checkbox toggle) so creation/deletion of the comment is
 * deferred until the Bemessung is saved.
 */
const useReconcilePlanComment = () => {
  const { enqueueSnackbar } = useSnackbar()
  const guidToElement = useGuidToElementIncludingRips()
  const { createComment: createLinkedComment, deleteComment: deleteLinkedComment } =
    useCommentMutations()

  // The linked comment should exist only when "Plan" is checked AND the Positionsliste text is
  // non-empty — an empty text removes the comment (there is nothing to show in the plan).
  const shouldPlanCommentExist = (showInPositionPlan: boolean, text: string): boolean =>
    showInPositionPlan && text.trim().length > 0

  // Whether the linked comment's existence differs from the desired state — i.e. submitting would
  // create or delete it. Used to treat a lone "Plan"/text change as a valid reason to save.
  const planCommentNeedsReconcile = useCallback(
    (elementGuid: string | undefined, showInPositionPlan: boolean, text: string): boolean =>
      !!elementGuid &&
      shouldPlanCommentExist(showInPositionPlan, text) !== !!findLinkedPlanComment(elementGuid),
    [],
  )

  const reconcilePlanComment = useCallback(
    async (elementGuid: string | undefined, showInPositionPlan: boolean, text: string) => {
      if (!elementGuid) return
      const linkedComment = findLinkedPlanComment(elementGuid)

      if (!shouldPlanCommentExist(showInPositionPlan, text)) {
        if (linkedComment) deleteLinkedComment(linkedComment.id)
        return
      }
      // Guard against creating a second linked comment for the same element.
      if (linkedComment) return

      const point = getShapeCenterPoint(guidToElement[elementGuid])
      if (!point) {
        enqueueSnackbar('Position des Elements konnte nicht bestimmt werden', { variant: 'error' })
        return
      }

      const preset = commentTypePresets[DEFAULT_NEW_COMMENT_TYPE]
      const elementStorey = guidToElement[elementGuid]?.storey
      await createLinkedComment({
        position: point,
        text: text.trim(),
        type: DEFAULT_NEW_COMMENT_TYPE,
        orientation: DEFAULT_COMMENT_ORIENTATION,
        color: preset.color,
        backgroundColor: preset.backgroundColor,
        showTitle: true,
        storeysForPlans: elementStorey ? [elementStorey] : [],
        showInPlans: preset.showInPlans,
        callouts: null,
        storeyForSceneRendering: elementStorey ?? null,
        element_guid: elementGuid,
      })
    },
    [guidToElement, createLinkedComment, deleteLinkedComment, enqueueSnackbar],
  )

  return { reconcilePlanComment, planCommentNeedsReconcile }
}

export default useReconcilePlanComment
